import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import { FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AccessibilityOutlinedIcon from '@mui/icons-material/AccessibilityOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import DateChip from '../moduleCard/DateChip';
import BodySilhouette from '../../../../components/bodySilhouette/BodySilhouette';
import { variantFromGender } from '../../../../components/bodySilhouette/bodySilhouetteFigures';
import { BODY_VIEWS, BODY_VIEW_OPTIONS, getRegionsForView } from '../../../../components/bodySilhouette/bodyRegions';
import { DataProviderInstance } from '../../../../api/DataProvider';
import BodyMapMarkerList from './BodyMapMarkerList';
import BodyMapMarkerPopup from '../../../../popup/BodyMapMarkerPopup/BodyMapMarkerPopup';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import { buildBodyMapJob } from '../../modules/reportSections';
import BodyMapReportTemplate from '../../../../components/documents-templates/BodyMapReportTemplate';
import {
  DEFAULT_MARKER_TYPE,
  MARKER_TYPES,
  createMarkerAtPoint,
  createMarkerForRegion,
  findOwnRecord,
  formatDate,
  markerSummary,
  markerTitle,
  moveMarker,
  regionLabelAtFraction,
  sortByDateDesc,
  toFigureMarkers,
} from './bodyMapMarkers';

const SAVE_DELAY_MS = 700;

const SAVE_LABELS = {
  pending: 'Alterações pendentes',
  saving: 'Salvando...',
  error: 'Não foi possível salvar. A próxima alteração tenta de novo.',
};

const LEGEND_CLASS = {
  complaint: styles.type_complaint,
  observation: styles.type_observation,
  assessed: styles.type_assessed,
};

/**
 * Módulo "Mapa corporal": marcadores numerados sobre a silhueta (frente e
 * costas), um mapa por atendimento, salvo a cada alteração.
 *
 * `data`: mapas do paciente (todos os atendimentos).
 * `gender`: sexo do cadastro, escolhe a silhueta.
 * `enabled`: false na aba Saúde do paciente (só leitura, com seleção de data).
 * `patientName`, `professionalId`, `companyConfig`: identificação no PDF.
 */
const BodyMap = ({ data, patientId, appointmentId, gender, enabled, collapsible = false, patientName, professionalId, companyConfig }) => {
  const { SetPatientBodyMap } = DataProviderInstance();
  const records = useMemo(() => sortByDateDesc(Array.isArray(data) ? data : []), [data]);
  const ownRecord = enabled ? findOwnRecord(records, appointmentId) : null;
  const previousRecords = enabled ? records.filter((record) => record !== ownRecord) : [];

  const [markers, setMarkers] = useState(ownRecord ? ownRecord.markers : []);
  const [view, setView] = useState(BODY_VIEWS.FRONT);
  const [activeType, setActiveType] = useState(DEFAULT_MARKER_TYPE);
  const [showPrevious, setShowPrevious] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  // Marcador aberto no popup de edição
  const [editingIndex, setEditingIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  // Arrasto solto, esperando confirmação: { index, x, y }
  const [pendingMove, setPendingMove] = useState(null);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const [savedAt, setSavedAt] = useState(null);

  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const saveRef = useRef(null);

  // Resincroniza com o servidor só quando não há alteração a caminho
  useEffect(() => {
    if (!enabled || pendingRef.current || busyRef.current) return;
    setMarkers(ownRecord ? ownRecord.markers : []);
  }, [ownRecord, enabled]);

  useEffect(() => {
    if (!enabled) setSelectedRecordId(records.length > 0 ? String(records[0].id) : null);
  }, [records, enabled]);

  function flush() {
    const payload = pendingRef.current;
    if (!payload || busyRef.current) return;
    pendingRef.current = null;
    busyRef.current = true;
    setSaveState('saving');
    SetPatientBodyMap(patientId, appointmentId, payload, (response) => {
      busyRef.current = false;
      if (response && response.status === 'SUCCESS') {
        setSaveState('saved');
        setSavedAt(new Date());
      } else {
        setSaveState('error');
      }
      // Alteração feita enquanto salvava: grava em seguida
      if (pendingRef.current) flush();
    });
  }
  saveRef.current = flush;

  // Ao sair da tela, grava o que ainda estiver pendente
  useEffect(() => {
    const timer = timerRef;
    const save = saveRef;
    return () => {
      clearTimeout(timer.current);
      if (save.current) save.current();
    };
  }, []);

  function commit(next) {
    setMarkers(next);
    pendingRef.current = next;
    setSaveState('pending');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveRef.current(), SAVE_DELAY_MS);
  }

  function addMarker(marker) {
    const next = [...markers, marker];
    commit(next);
    setSelectedIndex(next.length - 1);
  }

  function handlePick(point) {
    if (!enabled) return;
    addMarker(createMarkerAtPoint(view, point.x, point.y, activeType));
  }

  function handleAddRegion(key) {
    const region = getRegionsForView(view).find((item) => item.key === key);
    if (region) addMarker(createMarkerForRegion(region, activeType));
  }

  function handleChange(index, patch) {
    commit(markers.map((marker, i) => (i === index ? { ...marker, ...patch } : marker)));
  }

  function handleRemove(index) {
    commit(markers.filter((_, i) => i !== index));
    setSelectedIndex(null);
  }

  // Clique na lista ou no número: destaca na figura e, editando, abre o popup
  function handleSelect(index) {
    setSelectedIndex(index);
    const marker = visibleMarkers[index];
    if (marker && marker.view !== view) setView(marker.view);
    if (enabled) setEditingIndex(index);
  }

  function handleMarkerClick(key) {
    const [prefix, index] = key.split('-');
    if (prefix === 'own') handleSelect(Number(index));
  }

  // Mouse sobre um numero da figura: destaca o item da lista
  function handleMarkerHover(key) {
    const [prefix, index] = (key || '').split('-');
    setHoverIndex(prefix === 'own' ? Number(index) : null);
  }

  // Soltar o arrasto só pré-visualiza: o ponto novo vale depois de confirmar,
  // para um esbarrão no mouse não bagunçar o mapa
  function handleMarkerMove(key, point) {
    const [prefix, index] = key.split('-');
    if (prefix !== 'own') return;
    setPendingMove({ index: Number(index), x: point.x, y: point.y });
  }

  function confirmMove() {
    if (!pendingMove) return;
    commit(markers.map((marker, i) => (i === pendingMove.index ? moveMarker(marker, pendingMove.x, pendingMove.y) : marker)));
    setPendingMove(null);
  }

  const movingMarker = pendingMove ? markers[pendingMove.index] : null;
  const movedMarker = movingMarker ? moveMarker(movingMarker, pendingMove.x, pendingMove.y) : null;
  const moveMessage = movingMarker
    ? (markerTitle(movedMarker) !== markerTitle(movingMarker)
      ? `Mover o marcador ${pendingMove.index + 1} de ${markerTitle(movingMarker)} para ${markerTitle(movedMarker)}?`
      : `Mover o marcador ${pendingMove.index + 1} (${markerTitle(movingMarker)}) para o novo ponto?`)
    : '';

  const selectedRecord = !enabled
    ? records.find((record) => String(record.id) === selectedRecordId) || records[0] || null
    : null;
  const visibleMarkers = enabled ? markers : (selectedRecord ? selectedRecord.markers : []);
  // Na figura, o marcador em confirmação já aparece no ponto novo
  const figureOwnMarkers = movedMarker
    ? visibleMarkers.map((marker, i) => (i === pendingMove.index ? movedMarker : marker))
    : visibleMarkers;

  const figureMarkers = [
    ...(enabled && showPrevious
      ? previousRecords.flatMap((record) =>
        toFigureMarkers(record.markers, view, { prefix: `prev-${record.id}`, muted: true, title: formatDate(record.markedAt) }))
      : []),
    ...toFigureMarkers(figureOwnMarkers, view, { prefix: 'own', selectedIndex, highlightedIndex: hoverIndex }),
  ];

  const variant = variantFromGender(gender);

  const shownRecord = enabled ? ownRecord : selectedRecord;
  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: 'mapa-corporal',
    prepare: () => buildBodyMapJob({ markedAt: shownRecord ? shownRecord.markedAt : '', markers: visibleMarkers }, variant),
  });

  function renderSaveState() {
    if (saveState === 'saved' && savedAt) {
      return <span className={parts.muted}>Salvo às {savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>;
    }
    if (saveState === 'error') return <span className={styles.saveError}>{SAVE_LABELS.error}</span>;
    if (SAVE_LABELS[saveState]) return <span className={parts.muted}>{SAVE_LABELS[saveState]}</span>;
    return null;
  }

  return (
    <ModuleCard
      accent="pink"
      title="Mapa corporal"
      icon={<AccessibilityOutlinedIcon />}
      description="Pontos de queixa e observação marcados na silhueta"
      collapsible={collapsible}
      actions={visibleMarkers.length > 0 && (
        <CustomButton
          variant="outlined"
          style="secondary"
          label={report.exporting ? 'Gerando...' : 'PDF'}
          icon={<PictureAsPdfOutlinedIcon />}
          enabled={!report.exporting}
          onClick={report.start}
        />
      )}
    >
      <div className={styles.content}>
        <div className={styles.toolbar}>
          <div className={styles.context}>
            {enabled ? (
              <>
                <span className={parts.muted}>
                  {visibleMarkers.length === 0
                    ? 'Clique na figura para marcar um ponto'
                    : `${visibleMarkers.length} ${visibleMarkers.length === 1 ? 'marcador' : 'marcadores'} neste atendimento`}
                </span>
                {renderSaveState()}
              </>
            ) : (
              <>
                {selectedRecord && <DateChip label={formatDate(selectedRecord.markedAt)} />}
                {records.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="body-map-record-label">Atendimento</InputLabel>
                    <Select
                      labelId="body-map-record-label"
                      label="Atendimento"
                      value={selectedRecordId || ''}
                      onChange={(event) => { setSelectedRecordId(event.target.value); setSelectedIndex(null); }}
                    >
                      {records.map((record) => (
                        <MenuItem key={record.id} value={String(record.id)}>{formatDate(record.markedAt)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </div>

          <div className={styles.controls}>
            {enabled && previousRecords.length > 0 && (
              <FormControlLabel
                control={<Switch checked={showPrevious} onChange={(event) => setShowPrevious(event.target.checked)} size="small" />}
                label="Mostrar anteriores"
                sx={{ marginRight: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
              />
            )}

            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(event, value) => value && setView(value)}
            >
              {BODY_VIEW_OPTIONS.map((option) => (
                <ToggleButton key={option.key} value={option.key} className={parts.toggleButton}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
        </div>

        <div className={styles.layout}>
          <div className={styles.figureArea}>
            {enabled ? (
              <div className={styles.typeChips} role="radiogroup" aria-label="Tipo do próximo marcador">
                {MARKER_TYPES.map((type) => (
                  <button
                    key={type.key}
                    type="button"
                    role="radio"
                    aria-checked={activeType === type.key}
                    className={`${styles.typeChip} ${activeType === type.key ? styles.typeChipActive : ''}`}
                    onClick={() => setActiveType(type.key)}
                  >
                    <span className={`${styles.legendDot} ${LEGEND_CLASS[type.key]}`} />
                    {type.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className={styles.legend}>
                {MARKER_TYPES.map((type) => (
                  <span key={type.key} className={styles.legendItem}>
                    <span className={`${styles.legendDot} ${LEGEND_CLASS[type.key]}`} />
                    {type.label}
                  </span>
                ))}
              </div>
            )}
            <BodySilhouette
              variant={variant}
              view={view}
              markers={figureMarkers}
              onPick={enabled ? handlePick : undefined}
              pickLabel={enabled ? (point) => regionLabelAtFraction(view, point.x, point.y) : undefined}
              onMarkerClick={handleMarkerClick}
              onMarkerMove={enabled ? handleMarkerMove : undefined}
              onMarkerHover={handleMarkerHover}
              large
            />
            {enabled && <span className={styles.hint}>Escolha o tipo acima e clique na figura para marcar. Clique no marcador para detalhar, ou arraste para reposicionar.</span>}
          </div>

          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <span className={parts.sectionLabel}>Marcadores</span>
              {enabled && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="body-map-add-region-label">Adicionar região</InputLabel>
                  <Select
                    labelId="body-map-add-region-label"
                    label="Adicionar região"
                    value=""
                    onChange={(event) => handleAddRegion(event.target.value)}
                  >
                    {getRegionsForView(view).map((region) => (
                      <MenuItem key={region.key} value={region.key}>{region.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </div>

            <BodyMapMarkerList
              markers={visibleMarkers}
              view={view}
              selectedIndex={selectedIndex}
              hoverIndex={hoverIndex}
              enabled={enabled}
              onSelect={handleSelect}
              onHover={setHoverIndex}
            />

            {enabled && showPrevious && (
              <div className={styles.previous}>
                <span className={parts.sectionLabel}>Atendimentos anteriores</span>
                {previousRecords.map((record) => (
                  <div key={record.id} className={styles.previousRecord}>
                    <div className={styles.previousHead}>
                      <DateChip label={formatDate(record.markedAt)} compact />
                      <span className={parts.muted}>
                        {record.markers.length} {record.markers.length === 1 ? 'marcador' : 'marcadores'}
                      </span>
                    </div>
                    {record.markers.map((marker, index) => (
                      <span key={index} className={styles.previousLine}>
                        {markerTitle(marker)} · {markerSummary(marker)}{marker.note ? ` · ${marker.note}` : ''}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {editingIndex !== null && markers[editingIndex] && (
        <BodyMapMarkerPopup
          marker={markers[editingIndex]}
          number={editingIndex + 1}
          onSave={(patch) => handleChange(editingIndex, patch)}
          onRemove={() => handleRemove(editingIndex)}
          OnHide={() => setEditingIndex(null)}
        />
      )}

      {report.job && (
        <HiddenReport>
          <BodyMapReportTemplate ref={report.ref} {...report.paperProps} {...report.job} />
        </HiddenReport>
      )}

      <ConfirmDialog
        isOpen={Boolean(pendingMove)}
        variant="info"
        title="Mover marcador?"
        message={moveMessage}
        confirmLabel="Mover"
        cancelLabel="Deixar onde estava"
        onConfirm={confirmMove}
        onCancel={() => setPendingMove(null)}
      />
    </ModuleCard>
  );
};

export default BodyMap;
